import type { ComponentProps } from 'react';

import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/utils';
import { Button } from './button';
import { Input } from './input';
import { Textarea } from './textarea';

const inputGroupVariants = cva(
    cn(
        'group/input-group relative flex w-full items-center bg-input-background transition-[color,box-shadow] outline-none',
        'h-10 min-w-0 has-[>textarea]:h-auto',
        // keep the control from hugging an addon
        'has-[>[data-align=inline-start]]:[&>input]:pl-2',
        'has-[>[data-align=inline-end]]:[&>input]:pr-2',
        'has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col has-[>[data-align=block-start]]:[&>input]:pb-3',
        'has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-end]]:[&>input]:pt-3',
        // invalid lives on the group regardless of variant, matching the Input primitive's tokens
        'has-[[data-slot=input-group-control][aria-invalid=true]]:border-destructive',
        // Mirror the Input primitive's disabled treatment: dim the wrapper so the border matches a
        // plain disabled Input (which dims itself via disabled:opacity-50).
        'has-[[data-slot=input-group-control]:disabled]:pointer-events-none has-[[data-slot=input-group-control]:disabled]:opacity-50',
    ),
    {
        variants: {
            variant: {
                default: cn(
                    'rounded-md border border-input',
                    'has-[[data-slot=input-group-control]:focus-visible]:border-ring has-[[data-slot=input-group-control]:focus-visible]:shadow-[0_0_0_2px_var(--color-accent)]',
                    'has-[[data-slot=input-group-control][aria-invalid=true]:focus-visible]:border-destructive has-[[data-slot=input-group-control][aria-invalid=true]:focus-visible]:shadow-[0_0_0_2px_color-mix(in_oklab,var(--color-destructive)_20%,transparent)]',
                ),
                // Bottom rule only, square corners, no shadow; focus doesn't recolor the rule either.
                underline: 'rounded-none border-0 border-b border-input shadow-none',
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    },
);

function InputGroup({
    className,
    variant,
    ...props
}: ComponentProps<'div'> & VariantProps<typeof inputGroupVariants>) {
    return (
        <div
            data-slot="input-group"
            role="group"
            className={cn(inputGroupVariants({ variant }), className)}
            {...props}
        />
    );
}

const inputGroupAddonVariants = cva(
    'flex h-auto cursor-text items-center justify-center gap-2 py-1.5 text-sm font-medium text-muted-foreground select-none',
    {
        variants: {
            align: {
                'inline-start': 'order-first pl-3 has-[>button]:ml-[-0.45rem]',
                'inline-end': 'order-last pr-3 has-[>button]:mr-[-0.45rem]',
                'block-start': 'order-first w-full justify-start px-3 pt-3',
                'block-end': 'order-last w-full justify-start px-3 pb-3',
            },
        },
        defaultVariants: {
            align: 'inline-start',
        },
    },
);

function InputGroupAddon({
    className,
    align = 'inline-start',
    ...props
}: ComponentProps<'div'> & VariantProps<typeof inputGroupAddonVariants>) {
    return (
        <div
            role="group"
            data-slot="input-group-addon"
            data-align={align}
            className={cn(inputGroupAddonVariants({ align }), className)}
            onClick={(event) => {
                if ((event.target as HTMLElement).closest('button')) return;
                event.currentTarget.parentElement
                    ?.querySelector<HTMLElement>('input, textarea')
                    ?.focus();
            }}
            {...props}
        />
    );
}

function InputGroupButton({
    className,
    variant = 'ghost',
    size = 'sm',
    type = 'button',
    ...props
}: ComponentProps<typeof Button>) {
    return (
        <Button
            type={type}
            variant={variant}
            size={size}
            className={cn('gap-1.5', className)}
            {...props}
        />
    );
}

function InputGroupText({ className, ...props }: ComponentProps<'span'>) {
    return (
        <span
            className={cn(
                'flex items-center gap-2 text-sm text-muted-foreground [&_svg]:pointer-events-none [&_svg:not([class*=size-])]:size-4',
                className,
            )}
            {...props}
        />
    );
}

function InputGroupInput({ className, ...props }: ComponentProps<'input'>) {
    return (
        <Input
            className={cn(
                'h-full flex-1 rounded-none border-0 bg-transparent',
                'focus-visible:border-0 focus-visible:shadow-none',
                'aria-invalid:border-0 aria-invalid:shadow-none',
                'aria-invalid:focus-visible:border-0 aria-invalid:focus-visible:shadow-none dark:aria-invalid:focus-visible:shadow-none',
                // The group owns the disabled dim; keep the control at full opacity so it isn't dimmed twice.
                'disabled:opacity-100',
                className,
            )}
            {...props}
            data-slot="input-group-control"
        />
    );
}

function InputGroupTextarea({ className, ...props }: ComponentProps<'textarea'>) {
    return (
        <Textarea
            className={cn(
                'flex-1 resize-none rounded-none border-0 bg-transparent py-3 shadow-none',
                'focus-visible:border-0 focus-visible:shadow-none',
                'aria-invalid:border-0 aria-invalid:shadow-none',
                'aria-invalid:focus-visible:border-0 aria-invalid:focus-visible:shadow-none dark:aria-invalid:focus-visible:shadow-none',
                // The group owns the disabled dim; keep the control at full opacity so it isn't dimmed twice.
                'disabled:opacity-100',
                className,
            )}
            {...props}
            data-slot="input-group-control"
        />
    );
}

export {
    InputGroup,
    InputGroupAddon,
    InputGroupButton,
    InputGroupInput,
    InputGroupText,
    InputGroupTextarea,
};
